/**
 * Cashbox Open Page
 *
 * Mobile-first page for opening the cash register with initial amount.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, LockOpen, Calculator, Banknote } from 'lucide-react'

import api from '@/lib/api'
import { formatCurrency } from '@/lib/utils'
import { t } from '@/i18n'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Spinner } from '@/components/ui/spinner'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/components/ui/toaster'
import { useIsMobile } from '@/hooks/useIsMobile'

// Common Costa Rican bill denominations for quick amount buttons
const QUICK_AMOUNTS = [10000, 20000, 50000, 100000]

export function CashboxOpenPage() {
  const navigate = useNavigate()
  // En el celular el autofoco abre el teclado y tapa la mitad de la pantalla
  const isMobile = useIsMobile()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const [initialAmount, setInitialAmount] = useState('')
  const [notes, setNotes] = useState('')

  // Fetch current cashbox
  const { data: cashbox, isLoading } = useQuery({
    queryKey: ['my-cashbox'],
    queryFn: async () => {
      const response = await api.get('/cashboxes/my-cashbox')
      return response.data
    },
  })

  // Open cashbox mutation - creates a NEW session each time
  const openMutation = useMutation({
    mutationFn: async (data: { initialAmount: number; notes: string }) => {
      // Use the new /open endpoint that creates a new cashbox session
      const response = await api.post('/cashboxes/open', {
        initial_amount: data.initialAmount,
        notes: data.notes || t('cashbox.openNote'),
      })
      return response.data
    },
    onSuccess: async () => {
      // La pantalla de caja muestra el estado y la lista: sin esperar a que se
      // recarguen, aparecía la caja como estaba antes hasta refrescar.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['my-cashbox'] }),
        queryClient.invalidateQueries({ queryKey: ['cashbox-history'] }),
      ])
      toast({
        title: t('cashbox.opened'),
        description: t('cashbox.openedSuccess'),
        variant: 'success',
      })
      navigate('/cashbox')
    },
    onError: (error: Error) => {
      toast({
        title: t('common.error'),
        description: error.message || t('cashbox.openError'),
        variant: 'destructive',
      })
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const amount = parseFloat(initialAmount) || 0
    openMutation.mutate({ initialAmount: amount, notes })
  }

  const handleQuickAmount = (amount: number) => {
    setInitialAmount((prev) => {
      const current = parseFloat(prev) || 0
      return (current + amount).toString()
    })
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Skeleton className="h-10 w-10" />
          <Skeleton className="h-8 w-48" />
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  // If cashbox is already open, redirect
  if (cashbox?.is_open) {
    navigate('/cashbox')
    return null
  }

  const currentAmount = parseFloat(initialAmount) || 0

  return (
    <div className="space-y-4 pb-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="h-10 w-10 shrink-0"
          onClick={() => navigate('/cashbox')}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl md:text-2xl font-bold">{t('cashbox.openCashbox')}</h1>
          <p className="text-sm text-muted-foreground">{t('cashbox.enterInitialAmount')}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Amount Display */}
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="p-6 text-center">
            <p className="text-sm text-muted-foreground mb-2">{t('cashbox.initialAmount')}</p>
            <p className="text-4xl font-bold text-primary">
              {formatCurrency(currentAmount)}
            </p>
          </CardContent>
        </Card>

        {/* Amount Input */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Banknote className="h-4 w-4" />
              {t('cashbox.countCash')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="initialAmount">{t('cashbox.initialAmount')} *</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  ₡
                </span>
                <Input
                  id="initialAmount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={initialAmount}
                  onChange={(e) => setInitialAmount(e.target.value)}
                  placeholder="0"
                  inputMode="decimal"
                  className="pl-8 h-14 text-2xl font-bold"
                  autoFocus={!isMobile}
                />
              </div>
            </div>

            {/* Quick Amount Buttons */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">{t('cashbox.quickAdd')}</Label>
              <div className="grid grid-cols-4 gap-2">
                {QUICK_AMOUNTS.map((amount) => (
                  <Button
                    key={amount}
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleQuickAmount(amount)}
                  >
                    +{(amount / 1000).toFixed(0)}k
                  </Button>
                ))}
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="flex-1"
                  onClick={() => setInitialAmount('0')}
                >
                  <Calculator className="h-4 w-4 mr-1" />
                  {t('cashbox.startEmpty')}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="flex-1"
                  onClick={() => setInitialAmount('')}
                >
                  {t('payment.clear')}
                </Button>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">{t('cashbox.notes')}</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t('cashbox.openNotesPlaceholder')}
                className="resize-none"
                rows={2}
              />
            </div>
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <Button
            type="button"
            variant="outline"
            className="h-12 sm:flex-1"
            onClick={() => navigate('/cashbox')}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            className="h-12 sm:flex-1"
            disabled={openMutation.isPending}
          >
            {openMutation.isPending && <Spinner size="sm" className="mr-2" />}
            <LockOpen className="h-4 w-4 mr-2" />
            {t('cashbox.openCashbox')}
          </Button>
        </div>
      </form>
    </div>
  )
}
